import { useMutation, useQuery } from "react-query";
import {
  addUserToCompany,
  deleteCompanyUser,
  bulkAssignUsersToCompanies,
  getAccountUsers,
} from "../api/companyUsers";
import { queryClient } from "./client";
import { handleEventForTracking } from "../amplitudeAnalytics";
import { useGetUserProfile } from "./userProfile";

export function useAddUserToCompany() {
  return useMutation({
    mutationKey: "addUserToCompany",
    mutationFn: addUserToCompany,
    onSuccess: () => {
      queryClient.refetchQueries("userCompanies");
      handleEventForTracking({
        eventName: "addUserToCompany",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "addUserToCompany",
        success: false,
        eventType: "API",
      });
    },
  });
}

export function useAccountUsers() {
  return useQuery({
    queryKey: "accountUsers",
    queryFn: getAccountUsers,
  });
}

/**
 * Whether the currently logged-in user is part of the account-users list.
 * Gates visibility of the onboarding/user-management feature.
 *
 * Matched by email: account-users comes from taskManager (a different id-space
 * than the captable auth userId), and the auth store doesn't reliably carry the
 * email/userId, so the email from the user-profile query is the stable join key.
 */
export function useIsAccountUser() {
  const { data: accountUsers, isLoading } = useAccountUsers();
  const { data: userProfile } = useGetUserProfile();
  const email = (userProfile?.emailAddress || "").trim().toLowerCase();
  const isAccountUser = Boolean(
    email &&
      (accountUsers || []).some(
        (u: any) =>
          (u?.emailId || u?.email || "").trim().toLowerCase() === email
      )
  );
  return { isAccountUser, isLoading };
}

export function useBulkAssignUsers() {
  return useMutation({
    mutationKey: "bulkAssignUsers",
    mutationFn: bulkAssignUsersToCompanies,
    onSuccess: () => {
      queryClient.refetchQueries("userCompanies");
      handleEventForTracking({
        eventName: "bulkAssignUsers",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "bulkAssignUsers",
        success: false,
        eventType: "API",
      });
    },
  });
}

export function useDeleteCompanyUser() {
  return useMutation({
    mutationKey: "deleteCompanyUser",
    mutationFn: deleteCompanyUser,
    onSuccess: () => {
      queryClient.refetchQueries("userCompanies");
      handleEventForTracking({
        eventName: "deleteCompanyUser",
        success: true,
        eventType: "API",
      });
    },
    onError: () => {
      handleEventForTracking({
        eventName: "deleteCompanyUser",
        success: false,
        eventType: "API",
      });
    },
  });
}
